import { DAY_IDS } from './weekdays';
import type { ScheduleExceptionDraft, ScheduleGroup } from './workingHoursState';
import {
  dayOwner,
  getOffMask,
  isClosedState,
  isExplicit24x7State,
  type CardTarget,
  type WorkingHoursEditorState,
} from './workingHoursState';

export type EditorRenderMode = 'summary_24x7' | 'summary_closed' | 'cards';
export type ScheduleDisplayMode = 'summary_24x7' | 'summary_closed' | 'detailed';

export interface WorkingHoursEditorView {
  dayOwners: CardTarget[];
  offExceptions: ScheduleExceptionDraft[];
  showWeekendCard: boolean;
  renderMode: EditorRenderMode;
}

export function formatDateRu(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

// groups=[] now means "closed" (all days weekends) — only isExplicit24x7State matters for 24x7
export function deriveScheduleDisplayMode(groups: ScheduleGroup[]): ScheduleDisplayMode {
  if (isExplicit24x7State(groups, [])) return 'summary_24x7';
  if (isClosedState(groups, [])) return 'summary_closed';
  return 'detailed';
}

export function deriveWorkingHoursEditorView(state: WorkingHoursEditorState): WorkingHoursEditorView {
  const offExceptions = state.exceptions.filter(ex => ex.periodLocalId === 'off');

  // "isForced" = user explicitly wants the cards view (forceWeekendCard or a date was marked 'off')
  // Only this should override the summary modes.
  const isForced = state.forceWeekendCard || offExceptions.length > 0;
  const is24x7 = isExplicit24x7State(state.groups, state.exceptions);
  const isClosed = isClosedState(state.groups, state.exceptions);

  let renderMode: EditorRenderMode;
  if (isForced) {
    renderMode = 'cards';
  } else if (is24x7) {
    renderMode = 'summary_24x7';
  } else if (isClosed) {
    renderMode = 'summary_closed';
  } else {
    renderMode = 'cards';
  }

  // showWeekendCard = true whenever ANY day is unassigned (lives on the weekend card).
  // This drives both: hiding the "+ Выходные" button (it's already there) and
  // rendering the weekend ScheduleCard inside 'cards' mode.
  const hasWeekendDays = getOffMask(state.groups) !== 0;
  const showWeekendCard = state.forceWeekendCard || offExceptions.length > 0 || hasWeekendDays;

  return {
    dayOwners: DAY_IDS.map(day => dayOwner(state.groups, day)),
    offExceptions,
    showWeekendCard,
    renderMode,
  };
}
